import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Invitation, Member, Organisation, OrganisationRole } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head, Measure } from "@/ui/frame/Page";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { ErrorState, TableSkeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { OrgSettingsScreens } from "@/features/org/screens";
import { Dialog } from "@/ui/Overlay";
import { Field, Input } from "@/ui/Field";
import { Choice } from "@/ui/Choice";
import { Chip } from "@/ui/Chip";
import { FormError } from "@/features/auth/AuthCard";
import { useToast } from "@/ui/Toast";
import { useSession } from "@/lib/session";
import { relativeTime } from "@/lib/format";
import {
  ASSIGNABLE_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  canGrant,
  canManageMembers,
} from "@/features/org/roles";

export const Route = createFileRoute("/_app/o/$orgId/members")({
  component: MembersScreen,
});

function MembersScreen() {
  const { orgId } = Route.useParams();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);

  const organisation = useQuery({
    queryKey: ["organisation", orgId],
    queryFn: ({ signal }) =>
      api.get<{ organisation: Organisation }>(`/organisations/${orgId}`, undefined, signal),
  });

  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: ({ signal }) =>
      api.get<{ members: Member[] }>(`/organisations/${orgId}/members`, undefined, signal),
  });

  const invitations = useQuery({
    queryKey: ["invitations", orgId],
    queryFn: ({ signal }) =>
      api.get<{ invitations: Invitation[] }>(`/organisations/${orgId}/invitations`, undefined, signal),
  });

  const myRole = organisation.data?.organisation.role;
  const canManage = canManageMembers(myRole);

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrganisationRole }) =>
      api.patch<void>(`/organisations/${orgId}/members/${userId}`, { role }),
    onSuccess: async () => {
      toast.success("Role updated");
      await queryClient.invalidateQueries({ queryKey: ["members", orgId] });
    },
    onError: toast.error,
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/organisations/${orgId}/members/${userId}`),
    onSuccess: async () => {
      toast.success("Member removed");
      await queryClient.invalidateQueries({ queryKey: ["members", orgId] });
    },
    onError: toast.error,
  });

  const revokeInvitation = useMutation({
    mutationFn: (invitationId: string) =>
      api.delete<void>(`/organisations/${orgId}/invitations/${invitationId}`),
    onSuccess: async () => {
      toast.success("Invitation withdrawn");
      await queryClient.invalidateQueries({ queryKey: ["invitations", orgId] });
    },
    onError: toast.error,
  });

  return (
    <div className="flex flex-col gap-6">
      <Measure>
        <Head
          title="Members"
          description="Who can see and change things in this organisation."
          actions={
            canManage ? (
              <Button tone="primary" onClick={() => setInviting(true)}>
                Invite someone
              </Button>
            ) : undefined
          }
          screens={<OrgSettingsScreens />}
        />
        <div className="flex flex-col gap-8">
      <InviteDialog orgId={orgId} myRole={myRole} open={inviting} onOpenChange={setInviting} />

      {/* No heading. The page is called Members and this is the members —
          "People" above it was a second name for the same list. */}
      <Panel padded={false}>
        {members.isLoading && <TableSkeleton rows={3} columns={4} />}
        {members.isError && <ErrorState error={members.error} onRetry={() => void members.refetch()} />}

        {members.data && (
          <Table>
            <thead>
              <tr>
                <Th>Person</Th>
                <Th>Role</Th>
                <Th>Joined</Th>
                <Th align="right" quiet>{canManage ? "Actions" : ""}</Th>
              </tr>
            </thead>
            <tbody>
              {members.data.members.map((member) => {
                const isSelf = member.user_id === user?.id;
                const mayActOnThem = canManage && canGrant(myRole, member.role);

                return (
                  <Tr key={member.user_id}>
                    <Td>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {member.display_name || member.email}
                          {isSelf && <span className="ml-2 text-[var(--text-muted)]">(you)</span>}
                        </span>
                        <span className="mono text-[11px] text-[var(--text-muted)]">{member.email}</span>
                      </div>
                    </Td>
                    <Td>
                      {mayActOnThem ? (
                        <div className="w-44">
                          <Choice
                            value={member.role}
                            onValueChange={(role) =>
                              changeRole.mutate({ userId: member.user_id, role: role as OrganisationRole })
                            }
                            options={ASSIGNABLE_ROLES.filter((role) => canGrant(myRole, role)).map((role) => ({
                              value: role,
                              label: ROLE_LABELS[role],
                            }))}
                          />
                        </div>
                      ) : (
                        <Chip tone={member.role === "OWNER" ? "live" : undefined}>
                          {ROLE_LABELS[member.role]}
                        </Chip>
                      )}
                    </Td>
                    <Td className="text-[var(--text-secondary)]">{relativeTime(member.joined_at)}</Td>
                    <Td align="right">
                      {(mayActOnThem || (isSelf && member.role !== "OWNER")) && (
                        <Button
                          size="dense"
                          tone="ghost"
                          onClick={() => removeMember.mutate(member.user_id)}
                          busy={removeMember.isPending && removeMember.variables === member.user_id}
                        >
                          {isSelf ? "Leave" : "Remove"}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>

      {/* Only when somebody is actually waiting.
          A permanently drawn section headed "Pending invitations" with
          "No pending invitations" inside it is a card whose entire content
          is the restatement of its own title — on every visit, for every
          account that has finished inviting people. When there is nobody
          waiting the fact is worth one line under the list, not a section. */}
      {invitations.data && invitations.data.invitations.length === 0 && (
        <p className="t-meta">
          Nobody is waiting on an invitation. They expire after seven days and
          can only be accepted by the address they were sent to.
        </p>
      )}

      {(invitations.isLoading ||
        invitations.isError ||
        (invitations.data && invitations.data.invitations.length > 0)) && (
      <Panel
        title="Pending invitations"
        description="Invitations expire after seven days and can only be accepted by the address they were sent to."
        padded={false}
      >
        {invitations.isLoading && <TableSkeleton rows={2} columns={3} />}
        {invitations.isError && (
          <div className="px-5 py-4">
            <ErrorState error={invitations.error} compact onRetry={() => void invitations.refetch()} />
          </div>
        )}

        {invitations.data && invitations.data.invitations.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <Th>Address</Th>
                  <Th>Role</Th>
                  <Th>Expires</Th>
                  <Th align="right" quiet>{canManage ? "Actions" : ""}</Th>
                </tr>
              </thead>
              <tbody>
                {invitations.data.invitations.map((invitation) => (
                  <Tr key={invitation.id}>
                    <Td className="mono text-[13px]">{invitation.email}</Td>
                    <Td>
                      <Chip>{ROLE_LABELS[invitation.role]}</Chip>
                    </Td>
                    <Td className="text-[var(--text-secondary)]">{relativeTime(invitation.expires_at)}</Td>
                    <Td align="right">
                      {canManage && (
                        <Button
                          size="dense"
                          tone="ghost"
                          onClick={() => revokeInvitation.mutate(invitation.id)}
                          busy={revokeInvitation.isPending && revokeInvitation.variables === invitation.id}
                        >
                          Withdraw
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
        )}
      </Panel>
      )}
        </div>
      </Measure>
    </div>
  );
}

function InviteDialog({
  orgId,
  myRole,
  open,
  onOpenChange,
}: {
  orgId: string;
  myRole: OrganisationRole | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("DEVELOPER");

  const grantable = ASSIGNABLE_ROLES.filter((candidate) => canGrant(myRole, candidate));

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ invitation: Invitation }>(`/organisations/${orgId}/invitations`, { email, role }),
    onSuccess: async () => {
      toast.success("Invitation sent", `We emailed ${email}.`);
      await queryClient.invalidateQueries({ queryKey: ["invitations", orgId] });
      setEmail("");
      onOpenChange(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite someone"
      description="They will receive an email with a link that expires in seven days."
      footer={
        <>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={invite.isPending}
            disabled={!email.includes("@")}
            onClick={() => invite.mutate()}
          >
            Send invitation
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          invite.mutate();
        }}
      >
        <Field label="Email address" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Role" required hint={ROLE_DESCRIPTIONS[role as OrganisationRole]}>
          {({ id, describedBy }) => (
            <Choice
              id={id}
              value={role}
              onValueChange={setRole}
              aria-describedby={describedBy}
              options={grantable.map((candidate) => ({
                value: candidate,
                label: ROLE_LABELS[candidate],
                description: ROLE_DESCRIPTIONS[candidate],
              }))}
            />
          )}
        </Field>

        {invite.error ? <FormError error={invite.error} /> : null}
      </form>
    </Dialog>
  );
}
